import { MakeCallsFrom, PhoneNumberE164, TelecomPhoneLineId } from "@sp-crm/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import {
    BridgeCalling,
    CallFromSaveFailure,
    callUsingPhoneHint,
    useCallFrom,
} from "components/telecom/call-from-options";
import { InlineBanner } from "components/ui/inline-banner";
import { Radio } from "components/ui/radio";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useId } from "react";
import { PersonalNumberWarning, PhoneNumberChoice } from "./phone-number-choice";

const personalNumberWarningOf = (
    bridgeCalling: BridgeCalling,
): React.ReactElement | null => {
    if (bridgeCalling.bridgeCallPhoneNumber || bridgeCalling.ringPhoneNumber) {
        return null;
    }
    return bridgeCalling.personalPhoneNumber ? (
        <PersonalNumberWarning
            problem="Calls can't ring your personal number."
            fix="Update it in Personal settings"
        />
    ) : (
        <PersonalNumberWarning
            problem="No personal number is set, so calls can't ring your phone."
            fix="Add one in Personal settings"
        />
    );
};

const otherNumberWarningOf = (bridgeCalling: BridgeCalling): React.ReactElement | null =>
    bridgeCalling.bridgeCallPhoneNumber && !bridgeCalling.ringPhoneNumber ? (
        <InlineBanner type="warning">
            Calls can&apos;t ring this number. Enter a different one.
        </InlineBanner>
    ) : null;

interface MakeCallsUsingOptionsProps {
    preference: MakeCallsFrom;
    bridgeCalling: BridgeCalling;
    saveFailure: React.ReactElement | null;
    onChoose: (callFrom: MakeCallsFrom) => void;
    onCallYouAt: (phoneNumber: PhoneNumberE164) => Promise<void>;
    onCallPersonalNumber: () => Promise<void>;
}

export const MakeCallsUsingOptions: React.FC<MakeCallsUsingOptionsProps> = ({
    preference,
    bridgeCalling,
    saveFailure,
    onChoose,
    onCallYouAt,
    onCallPersonalNumber,
}) => {
    const radioGroupId = useId();
    const chooseBrowser = useCallback(() => onChoose("browser"), [onChoose]);
    const choosePhone = useCallback(() => onChoose("phone"), [onChoose]);

    return (
        <div className="space-y-2">
            <div
                role="radiogroup"
                aria-labelledby={`${radioGroupId}-label`}
                className="space-y-2">
                <div id={`${radioGroupId}-label`}>Make calls using</div>
                <Radio
                    name={radioGroupId}
                    checked={preference === "browser"}
                    onChange={chooseBrowser}
                    label="Your browser"
                />
                <Radio
                    name={radioGroupId}
                    checked={preference === "phone"}
                    onChange={choosePhone}
                    label="Your phone"
                />
            </div>
            {saveFailure}
            {preference === "phone" ? (
                <div className="ml-6 space-y-2">
                    <p className="text-sm text-gray-500">{callUsingPhoneHint}</p>
                    <PhoneNumberChoice
                        label="Number to ring"
                        otherPhoneNumber={bridgeCalling.bridgeCallPhoneNumber}
                        personalPhoneNumber={bridgeCalling.personalPhoneNumber}
                        personalNumberWarning={personalNumberWarningOf(bridgeCalling)}
                        otherNumberWarning={otherNumberWarningOf(bridgeCalling)}
                        saveFailureMessage="Unable to save the phone number."
                        onUseOtherNumber={onCallYouAt}
                        onUsePersonalNumber={onCallPersonalNumber}
                    />
                </div>
            ) : null}
        </div>
    );
};

interface MakeCallsUsingSettingProps {
    phoneLineId: TelecomPhoneLineId;
}

export const MakeCallsUsingSetting: React.FC<MakeCallsUsingSettingProps> = ({
    phoneLineId,
}) => {
    const choice = useCallFrom(phoneLineId);
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const setNumber = useMutation(
        trpcClient.telecom.setBridgeCallPhoneNumber.mutationOptions(),
    );
    const clearNumber = useMutation(
        trpcClient.telecom.clearBridgeCallPhoneNumber.mutationOptions(),
    );

    const reload = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: trpcClient.telecom.getBridgeCalling.queryKey({ phoneLineId }),
            }),
        [queryClient, trpcClient, phoneLineId],
    );

    const callYouAt = useCallback(
        async (bridgeCallPhoneNumber: PhoneNumberE164) => {
            try {
                await setNumber.mutateAsync({ phoneLineId, bridgeCallPhoneNumber });
            } finally {
                await reload();
            }
        },
        [phoneLineId, setNumber, reload],
    );

    const callPersonalNumber = useCallback(async () => {
        try {
            await clearNumber.mutateAsync({ phoneLineId });
        } finally {
            await reload();
        }
    }, [phoneLineId, clearNumber, reload]);

    switch (choice.status) {
        case "loading":
            return <Spinner />;
        case "error":
            return (
                <RetryableErrorBanner
                    message="Unable to load how you make calls."
                    retrying={choice.retrying}
                    onRetry={choice.retry}
                />
            );
        case "ready":
            return (
                <MakeCallsUsingOptions
                    preference={choice.preference}
                    bridgeCalling={choice.bridgeCalling}
                    saveFailure={choice.saveFailed ? <CallFromSaveFailure /> : null}
                    onChoose={choice.chooseCallFrom}
                    onCallYouAt={callYouAt}
                    onCallPersonalNumber={callPersonalNumber}
                />
            );
    }
    const exhaustiveCheck: never = choice;
    throw new Error(`Unhandled call-from status: ${JSON.stringify(exhaustiveCheck)}`);
};
